import { motion } from 'motion/react'
import Image from 'next/image'
import { useCallback, useMemo } from 'react'
import { Button } from '@/src/components/ui/button'
import { useCopyToClipboardWithDelay } from '@/src/hooks/use-copy-to-clipboard-with-delay'

const baseUrl =
  process.env.APP_BASE_URL ||
  (process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : undefined)

interface ShowReferralCodeProps {
  referralCode: string
  referredUsersCount: number
}

export const ShowReferralCode = ({
  referralCode,
  referredUsersCount,
}: ShowReferralCodeProps) => {
  const { mutate: copyTransactionHash, isHolding } =
    useCopyToClipboardWithDelay()

  const referralLink = useMemo(
    () => `${baseUrl}/invite/${referralCode}`,
    [referralCode],
  )

  const onCopy = useCallback(() => {
    copyTransactionHash(referralLink)
  }, [referralLink, copyTransactionHash])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='flex flex-col'
    >
      {referredUsersCount > 0 ? (
        <>
          <p className='text-center text-secondary text-base mb-2'>
            You have referred
          </p>
          <h5 className='text-5xl font-extralight font-serif text-center mb-7'>
            {referredUsersCount}{' '}
            {referredUsersCount === 1 ? 'pioneer' : 'pioneers'}
          </h5>
        </>
      ) : (
        <>
          <h5 className='text-5xl font-extralight font-serif text-center mb-2'>
            You&apos;re all set
          </h5>
          <p className='text-center text-secondary text-base mb-10'>
            Spread the word and track your referrals here. If you trade over
            $1K, future perks may be tied to early activity.
          </p>
        </>
      )}

      <Image
        src='https://s3.polaris.app/illustrations/referral-birds.png'
        className='mx-auto mb-10 flex-shrink-0'
        alt='Referral birds'
        width={208}
        height={106}
        style={{
          aspectRatio: '208.24/106.00',
        }}
      />

      <div className='flex flex-col gap-2 mb-10'>
        <label
          className='text-sm leading-5 text-secondary'
          htmlFor='referralLink'
        >
          Share your link
        </label>
        <div className='flex gap-2 px-5 py-3 items-center justify-between border-input-border border h-14 rounded-2xl relative'>
          <p
            className='text-base text-input-text overflow-hidden whitespace-nowrap'
            style={{ direction: 'rtl', textAlign: 'left' }}
          >
            <span style={{ direction: 'ltr' }}>{referralLink}</span>
          </p>
        </div>
        <p className='mb-5 text-secondary text-sm leading-5'>
          When you share a URL from anywhere in Polaris, your referral code will
          be linked.
        </p>
        <Button
          variant='primary'
          size='md'
          className='w-full'
          onClick={onCopy}
          disabled={isHolding}
        >
          {!isHolding ? (
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
              transition={{ duration: 0.1 }}
            >
              Copy link
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
              transition={{ duration: 0.1 }}
            >
              Copied to clipboard
            </motion.div>
          )}
        </Button>
      </div>
    </motion.div>
  )
}
